import { useState } from "react";
import { Star } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { rateEvent, clearRating, removeVisit } from "@/lib/user-data.functions";

interface Props {
  eventId: string;
  visited: boolean;
  rating: number | null;
  onChange: (next: { visited: boolean; rating: number | null }) => void;
}

/**
 * Compact rating control shown on event cards for signed-in users.
 * Rating a place implies "been there" — a visit is auto-logged server-side.
 * Hovering previews the rating with filled stars; clicking commits it
 * with a brief scale pulse for tactile feedback.
 */
export function EventUserActions({ eventId, visited, rating, onChange }: Props) {
  const rateFn = useServerFn(rateEvent);
  const clearFn = useServerFn(clearRating);
  const removeVisitFn = useServerFn(removeVisit);
  const [hover, setHover] = useState<number | null>(null);
  const [justClicked, setJustClicked] = useState<number | null>(null);
  const [pending, setPending] = useState(false);

  const display = hover ?? rating ?? 0;
  const isRated = rating != null;

  const setRating = async (next: number) => {
    if (pending) return;
    setPending(true);
    setJustClicked(next);
    setTimeout(() => setJustClicked(null), 220);
    const prevRating = rating;
    const prevVisited = visited;
    onChange({ visited: true, rating: next });
    try {
      const res = await rateFn({ data: { eventId, rating: next } });
      if (!res.ok) {
        onChange({ visited: prevVisited, rating: prevRating });
        toast.error(res.error || "Couldn't save rating");
      } else {
        toast.success(`Rated ${next} star${next > 1 ? "s" : ""}`);
      }
    } catch {
      onChange({ visited: prevVisited, rating: prevRating });
      toast.error("Couldn't save rating");
    }
    setPending(false);
  };

  const clear = async () => {
    if (pending) return;
    setPending(true);
    const prevRating = rating;
    const prevVisited = visited;
    onChange({ visited: false, rating: null });
    try {
      await clearFn({ data: { eventId } });
      await removeVisitFn({ data: { eventId } });
      toast.success("Cleared");
    } catch {
      onChange({ visited: prevVisited, rating: prevRating });
      toast.error("Couldn't clear");
    }
    setPending(false);
  };

  return (
    <div className="mt-2.5 flex items-center gap-2 flex-wrap print:hidden">
      <div
        className="inline-flex items-center"
        role="radiogroup"
        aria-label="Rate this place"
        onMouseLeave={() => setHover(null)}
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= display;
          return (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onFocus={() => setHover(n)}
              onBlur={() => setHover(null)}
              disabled={pending}
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
              aria-pressed={rating === n}
              className="p-0.5 transition-transform duration-150 hover:scale-125 active:scale-95 disabled:opacity-60"
              style={{
                transform: justClicked != null && n <= justClicked ? "scale(1.35)" : undefined,
              }}
            >
              <Star
                className="h-5 w-5 transition-colors"
                fill={filled ? "currentColor" : "none"}
                strokeWidth={filled ? 1.5 : 2}
                style={{
                  color: filled
                    ? "var(--color-highlight)"
                    : "color-mix(in oklab, var(--color-foreground) 35%, transparent)",
                }}
              />
            </button>
          );
        })}
      </div>
      {isRated && (
        <button
          type="button"
          onClick={clear}
          disabled={pending}
          className="text-[11px] text-muted-foreground hover:text-destructive inline-flex items-center gap-0.5 disabled:opacity-60"
          aria-label="Clear rating"
        >
          clear
        </button>
      )}
    </div>
  );
}